import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { General } from 'icons/untitledui';
import useDropdownDirection from 'shared/hooks/useDropdownDirection';
import * as Styled from 'theme/components/SelectionDropdown.style';
import {
  BaseSelectionDropdown,
  type BaseDropdownOption,
} from './BaseSelectionDropdown';

export type MultiSelectionDropdownOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

interface MultiSelectionDropdownProps {
  ariaLabel: string;
  value: string[] | null | undefined;
  options: MultiSelectionDropdownOption[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  /** Defaults to 298. Pass `null` for no max-width (full-width trigger). */
  maxWidth?: number | null;
  popoverMinWidth?: number;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  renderValue?: (selectedLabels: string[]) => React.ReactNode;
}

export function MultiSelectionDropdown({
  ariaLabel,
  value,
  options,
  onChange,
  placeholder = 'Select',
  maxWidth = 298,
  popoverMinWidth,
  isOpen: controlledOpen,
  onOpenChange: externalOnOpenChange,
  renderValue,
}: MultiSelectionDropdownProps): JSX.Element {
  const [trackedOpen, setTrackedOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const onOpenChange = useCallback(
    (next: boolean) => {
      setTrackedOpen(next);
      externalOnOpenChange?.(next);
    },
    [externalOnOpenChange]
  );

  const open = controlledOpen ?? trackedOpen;

  const { openUp, anchorRect } = useDropdownDirection({
    open,
    triggerRef,
    listRef,
  });

  const selectedValues = useMemo(() => value ?? [], [value]);

  const selectedLabels = useMemo(() => {
    if (!selectedValues.length) return [];
    const map = new Map(options.map((o) => [o.value, o.label]));
    return selectedValues.map((v) => map.get(v) ?? v);
  }, [options, selectedValues]);

  const displayValue = useMemo(() => {
    if (!selectedValues.length) return placeholder;
    if (renderValue) return renderValue(selectedLabels);
    return selectedLabels.join(', ');
  }, [placeholder, renderValue, selectedLabels, selectedValues.length]);

  const popoverStyle = useMemo<React.CSSProperties>(() => {
    const EDGE_PADDING = 16;
    const GAP = 10;

    if (typeof window === 'undefined') return {};

    const rect = anchorRect ?? triggerRef.current?.getBoundingClientRect();
    if (!rect) {
      return { position: 'fixed', left: EDGE_PADDING, top: EDGE_PADDING };
    }

    const { width, left: rectLeft, top: rectTop, bottom: rectBottom } = rect;
    const effectiveWidth = popoverMinWidth
      ? Math.max(width, popoverMinWidth)
      : width;
    const maxLeft = window.innerWidth - EDGE_PADDING - effectiveWidth;
    const left = Math.max(EDGE_PADDING, Math.min(rectLeft, maxLeft));

    if (openUp) {
      return {
        position: 'fixed',
        left,
        width: effectiveWidth,
        bottom: window.innerHeight - rectTop + GAP,
      };
    }

    return {
      position: 'fixed',
      left,
      width: effectiveWidth,
      top: rectBottom + GAP,
    };
  }, [anchorRect, openUp, popoverMinWidth]);

  const isSelected = useCallback(
    (opt: BaseDropdownOption) => selectedValues.includes(opt.value),
    [selectedValues]
  );

  const onOptionSelect = useCallback(
    (opt: BaseDropdownOption) => {
      const selected = selectedValues.includes(opt.value);
      const next = selected
        ? selectedValues.filter((v) => v !== opt.value)
        : [...selectedValues, opt.value];
      onChange(next);
    },
    [onChange, selectedValues]
  );

  const renderOptionCheck = useCallback(
    (selected: boolean) => (
      <Styled.OptionCheck $selected={selected}>
        {selected ? <General.Check size={16} /> : null}
      </Styled.OptionCheck>
    ),
    []
  );

  const renderPopover = useCallback(
    ({
      listRef: baseListRef,
      ariaLabel: label,
      optionElements,
    }: {
      listRef: React.RefObject<HTMLDivElement | null>;
      ariaLabel: string;
      optionElements: React.ReactNode;
    }) =>
      createPortal(
        <Styled.Popover
          ref={popoverRef}
          role="listbox"
          aria-label={label}
          aria-multiselectable="true"
          $openUp={openUp}
          $maxWidth={maxWidth}
          style={popoverStyle}
        >
          <Styled.List ref={baseListRef}>{optionElements}</Styled.List>
        </Styled.Popover>,
        document.body
      ),
    [maxWidth, openUp, popoverStyle]
  );

  return (
    <BaseSelectionDropdown
      ariaLabel={ariaLabel}
      options={options}
      displayValue={displayValue}
      isOpen={controlledOpen}
      onOpenChange={onOpenChange}
      triggerRef={triggerRef}
      listRef={listRef}
      triggerStyleProps={{ $openUp: openUp, $maxWidth: maxWidth }}
      additionalClickOutsideRefs={[popoverRef]}
      isSelected={isSelected}
      onOptionSelect={onOptionSelect}
      closeOnSelect={false}
      renderOptionCheck={renderOptionCheck}
      renderPopover={renderPopover}
    />
  );
}
